import { translate } from '@avensia/excite';
import { TestIdProps } from '@avensia/test-automation';
import { styled } from '@glitz/react';
import * as React from 'react';
import { overflowEllipsisDecorator } from 'webapp/decorators/text';
import { StatusCheck } from 'webapp/product/StatusCheck';
import { a11yGrey } from 'webapp/shared/sharedTheme';

type StockStatus = NordicNest.Api.StockStatus;
type ProductStatus = NordicNest.Api.ProductStatus;

type Props = {
  variantStatus: ProductStatus;
  stockStatus: StockStatus;
  deliveryTime?: string;
  pdpstyling?: boolean;
  productCardStyling?: boolean;
};

export const StockStatus = styled(
  React.memo(({ variantStatus, stockStatus, deliveryTime, pdpstyling, productCardStyling }: Props) => {
    const getTranslation = React.useCallback((key: StockStatus, variantStatus: ProductStatus) => {
      if (variantStatus === 'blocked') return translate(t => t.productStockStatus.outOfStock);

      switch (variantStatus) {
        case 'active':
          switch (key) {
            case 'inStock':
              return translate(t => t.productStockStatus.inStockActive);
            case 'lowStock':
              return translate(t => t.productStockStatus.lowStockActive);
            case 'preOrdered':
              return translate(t => t.productStockStatus.preOrderedActive);
            case 'backOrdered':
              return translate(t => t.productStockStatus.backOrderedActive);
            case 'outOfStock':
              return translate(t => t.productStockStatus.outOfStockActive);
            case 'unknown':
            default:
              return translate(t => t.productStockStatus.unknownActive);
          }
        case 'endOfLife':
          switch (key) {
            case 'inStock':
              return translate(t => t.productStockStatus.inStockEol);
            case 'lowStock':
              return translate(t => t.productStockStatus.lowStockEol);
            case 'preOrdered':
              return translate(t => t.productStockStatus.preOrderedEol);
            case 'backOrdered':
              return translate(t => t.productStockStatus.discontinuedEol);
            case 'outOfStock':
              return translate(t => t.productStockStatus.discontinuedEol);
            case 'unknown':
            default:
              return translate(t => t.productStockStatus.discontinuedEol);
          }
        case 'temporarilyOutOfStock':
          switch (key) {
            case 'inStock':
              return translate(t => t.productStockStatus.inStockTup);
            case 'lowStock':
              return translate(t => t.productStockStatus.lowStockTup);
            case 'preOrdered':
              return translate(t => t.productStockStatus.preOrderedTup);
            case 'backOrdered':
              return translate(t => t.productStockStatus.backOrderedTup);
            case 'outOfStock':
              return translate(t => t.productStockStatus.outOfStockTup);
            case 'unknown':
            default:
              return translate(t => t.productStockStatus.unknownTup);
          }
        case 'news':
        default:
          switch (key) {
            case 'inStock':
              return translate(t => t.productStockStatus.inStock);
            case 'lowStock':
              return translate(t => t.productStockStatus.lowStock);
            case 'preOrdered':
              return translate(t => t.productStockStatus.preOrdered);
            case 'backOrdered':
              return translate(t => t.productStockStatus.backOrdered);
            case 'outOfStock':
              return translate(t => t.productStockStatus.outOfStock);
            case 'unknown':
            default:
              return translate(t => t.productStockStatus.unknown);
          }
      }
    }, []);

    const message: string[] = [getTranslation(stockStatus, variantStatus)].filter(m => m.trimStart().length > 0);

    if (deliveryTime && stockStatus !== 'outOfStock' && stockStatus !== 'inStock') {
      message.push(deliveryTime);
    }

    return (
      <Base css={productCardStyling && productCardStylingDecorator}>
        {pdpstyling ? (
          <Message css={msgGap}>
            <StatusCheck css={indiciator} stockStatus={stockStatus} variantStatus={variantStatus} />
            <span>
              <Bold>{message[0]}</Bold>
              {message.length > 1 && <span>, {message.slice(1).join(', ')}</span>}
            </span>
          </Message>
        ) : (
          <Message css={productCardStyling && productCardStylingDecorator} {...TestIdProps.StockStatus}>
            <StatusCheck stockStatus={stockStatus} variantStatus={variantStatus} />
            <styled.Span css={productCardStyling && overflowEllipsisDecorator}> {message.join(', ')}</styled.Span>
          </Message>
        )}
      </Base>
    );
  }),
);

const msgGap = styled({ gap: t => t.sharedSpacing.m });
const indiciator = styled({ width: 24, marginRight: 0 });

const productCardStylingDecorator = styled({
  overflow: 'hidden',
  color: a11yGrey,
});

const Base = styled.div({
  fontSize: t => t.sharedFontSize.xxs,
  display: 'flex',
  alignItems: 'center',
});

const Message = styled.div({
  ['text-wrap' as string]: 'balance',
  display: 'flex',
  alignItems: 'center',
  margin: {
    bottom: t => t.spacing.s,
  },
  ':last-child': {
    margin: {
      bottom: 0,
    },
  },
});

const Bold = styled.span({
  fontWeight: 400,
});
